<template>
  <v-container class="text-start" fluid>
    <v-row justify="center" class="mt-12 mb-8">
      <v-col cols="12" xl="10">
        <!-- A book without a Shopify order: staff enter the horoscope data here and
             the book then runs through the same pipeline as an ordered one. -->
        <v-sheet elevation="1" rounded class="pa-6">
          <v-row align="center" no-gutters class="mb-4">
            <h2 class="text-h5 mb-0">{{ $t('Manual books') }}</h2>
            <v-spacer></v-spacer>
            <v-select
              v-model="selectedStore"
              :items="stores"
              item-text="domain"
              return-object
              dense
              hide-details
              style="max-width: 320px"
              :label="$t('Select Store')"
              prepend-icon="mdi-city-variant-outline"
              @change="fetchOrders"
            ></v-select>
          </v-row>

          <v-form ref="form" v-model="valid" @submit.prevent="create">
            <v-alert v-if="addingTo" type="info" dense text class="mb-4">
              <div class="d-flex align-center flex-wrap">
                <span>
                  {{ $t('Adding a book to order {0} ({1} so far). Customer and shipping address are the order\'s.', [addingTo.orderNumber, (addingTo.books || []).length]) }}
                </span>
                <v-spacer></v-spacer>
                <v-btn small text color="primary" @click="finishAdding">{{ $t('Done') }}</v-btn>
              </div>
            </v-alert>
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.orderNumber"
                  :disabled="!!addingTo"
                  :label="$t('Order number')"
                  :rules="[required]"
                  placeholder="1001"
                  hint="CPI title id and drop-ship number; also in the QR code"
                  persistent-hint
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.variant"
                  :items="designs"
                  :label="$t('Cover')"
                  :rules="[required]"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.bookType"
                  :items="bookTypeItems"
                  :label="$t('Book type')"
                  :hint="$t('Empty: the design\'s type (personality for the editorial designs)')"
                  persistent-hint
                  clearable
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="5" class="d-flex align-center">
                <span class="text--secondary mr-4">{{ $t('Delivery') }}</span>
                <v-checkbox
                  v-model="form.delivery.print"
                  :label="$t('Print')"
                  :disabled="!!addingTo && !addingTo.shippingAddress"
                  :title="addingTo && !addingTo.shippingAddress ? $t('This order has no shipping address') : ''"
                  class="mt-0 mr-6"
                  hide-details
                ></v-checkbox>
                <v-checkbox
                  v-model="form.delivery.digital"
                  :label="$t('Digital')"
                  class="mt-0"
                  hide-details
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-subheader class="pl-0">{{ $t('Horoscope') }}</v-subheader>
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.name"
                  :label="$t('Name')"
                  :rules="[required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="form.birthDate"
                  :label="$t('Birth date')"
                  :rules="[required, dateRule]"
                  placeholder="DD.MM.YYYY"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="form.birthTime"
                  :label="$t('Birth time')"
                  :rules="[timeRule]"
                  placeholder="HH:mm"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <birth-place-field
                  v-model="form.birthPlace"
                  :label="$t('Birth place')"
                  :rules="[required]"
                  placeholder="Berlin, Deutschland"
                ></birth-place-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="form.gender"
                  :items="genderItems"
                  :label="$t('Gender')"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-row v-if="isPartners">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.namePartner"
                  :label="$t('Partner name')"
                  :rules="[required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="form.birthDatePartner"
                  :label="$t('Partner birth date')"
                  :rules="[required, dateRule]"
                  placeholder="DD.MM.YYYY"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="form.birthTimePartner"
                  :label="$t('Birth time')"
                  :rules="[timeRule]"
                  placeholder="HH:mm"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <birth-place-field
                  v-model="form.birthPlacePartner"
                  :label="$t('Partner birth place')"
                  :rules="[required]"
                ></birth-place-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="form.genderPartner"
                  :items="genderItems"
                  :label="$t('Partner gender')"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-textarea
              v-model="form.greetingMessage"
              :label="$t('Greeting message')"
              rows="2"
              auto-grow
              outlined
              dense
            ></v-textarea>

            <template v-if="!addingTo">
            <v-subheader class="pl-0">{{ $t('Customer') }}</v-subheader>
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.customer.firstName"
                  :label="$t('First name')"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.customer.lastName"
                  :label="$t('Last name')"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.customer.email"
                  :label="$t('Email')"
                  :rules="form.delivery.digital ? [required, emailRule] : [emailRule]"
                  hint="The digital book is sent here"
                  persistent-hint
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- CPI ships the printed book directly, so print needs an address. -->
            <template v-if="form.delivery.print">
              <v-subheader class="pl-0">{{ $t('Shipping address') }}</v-subheader>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="form.shipping.firstName"
                    :label="$t('First name')"
                    :placeholder="form.customer.firstName"
                    :rules="[shippingName('firstName')]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="form.shipping.lastName"
                    :label="$t('Last name')"
                    :placeholder="form.customer.lastName"
                    :rules="[shippingName('lastName')]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.shipping.address1"
                    :label="$t('Address')"
                    :rules="[required]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="form.shipping.address2"
                    :label="$t('Address line 2')"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="4" md="2">
                  <v-text-field
                    v-model="form.shipping.zip"
                    :label="$t('ZIP')"
                    :rules="[required]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="8" md="3">
                  <v-text-field
                    v-model="form.shipping.city"
                    :label="$t('City')"
                    :rules="[required]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="8" md="2">
                  <v-text-field
                    v-model="form.shipping.country"
                    :label="$t('Country')"
                    :rules="[required]"
                    placeholder="Deutschland"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="4" md="2">
                  <v-text-field
                    v-model="form.shipping.countryCode"
                    :label="$t('Country code')"
                    :rules="[required, countryCodeRule]"
                    placeholder="DE"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </template>
            </template>

            <div class="d-flex align-center">
              <v-btn
                type="submit"
                color="primary"
                :loading="creating"
                :disabled="!valid || !hasDelivery || !selectedStore"
              >
                <v-icon left>mdi-book-plus-outline</v-icon>
                {{ addingTo ? $t('Add book to order') : $t('Create book') }}
              </v-btn>
              <span v-if="!hasDelivery" class="warning--text ml-4">
                {{ $t('Choose print, digital or both') }}
              </span>
            </div>
          </v-form>
        </v-sheet>

        <v-sheet elevation="1" rounded class="pa-6 mt-6">
          <h3 class="text-h6 mb-2">{{ $t('Existing manual books') }}</h3>
          <v-data-table
            :headers="headers"
            :items="orders"
            :loading="loadingOrders"
            item-key="orderId"
            disable-sort
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50] }"
          >
            <template v-slot:[`item.orderNumber`]="{ item }">
              <span class="text-no-wrap">
                {{ item.orderNumber }}
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      :aria-label="$t('Change order number')"
                      v-bind="attrs"
                      v-on="on"
                      @click="startRename(item)"
                    >
                      <v-icon x-small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Change order number') }}</span>
                </v-tooltip>
              </span>
            </template>
            <template v-slot:[`item.name`]="{ item }">
              {{ bookOf(item).properties && bookOf(item).properties.name }}
              <v-chip v-if="(item.books || []).length > 1" x-small class="ml-1">
                +{{ item.books.length - 1 }}
              </v-chip>
            </template>
            <template v-slot:[`item.variant`]="{ item }">
              {{ bookOf(item).variant }}
            </template>
            <template v-slot:[`item.tracks`]="{ item }">
              {{ (bookOf(item).tracks || []).map((t) => $t(capitalize(t))).join(' + ') }}
            </template>
            <template v-slot:[`item.stage`]="{ item }">
              <v-chip x-small :color="stageColor(bookOf(item))" dark>
                {{ bookOf(item).stage }}
              </v-chip>
            </template>
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ item.createdAt ? formatDate(item.createdAt) : '' }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <!-- Same two buttons as ManageOrders: greyed when the book was not bought with that delivery. -->
              <div class="d-flex justify-end">
              <horoscope-book-dialog
                :ref="`print-${item.orderId}`"
                :order="dialogOrder(item)"
                :store="selectedStore"
                track="print"
              >
                <template v-slot:activator="{ on: onDialog, attrs }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :disabled="!has(item, 'print')"
                        color="error"
                        v-bind="attrs"
                        v-on="{ ...onTooltip, ...onDialog }"
                      >
                        <v-icon>mdi-printer-wireless</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Horoscope Book') }}</span>
                  </v-tooltip>
                </template>
              </horoscope-book-dialog>
              <horoscope-book-dialog
                :ref="`digital-${item.orderId}`"
                :order="dialogOrder(item)"
                :store="selectedStore"
                track="digital"
              >
                <template v-slot:activator="{ on: onDialog, attrs }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :disabled="!has(item, 'digital')"
                        color="error"
                        v-bind="attrs"
                        v-on="{ ...onTooltip, ...onDialog }"
                      >
                        <v-icon>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Digital Horoscope Book') }}</span>
                  </v-tooltip>
                </template>
              </horoscope-book-dialog>
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="another.show" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ $t('Book {0} created', [another.bookNumber]) }}</v-card-title>
        <v-card-text>
          {{ $t('Add another book to order {0}? The order number, customer and shipping address are kept.', [another.order && another.order.orderNumber]) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="declineAnother">{{ $t('No, open the book') }}</v-btn>
          <v-btn color="primary" @click="acceptAnother">{{ $t('Add another book') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rename.show" max-width="420" @keydown.esc="rename.show = false">
      <v-card>
        <v-card-title class="text-h6">{{ $t('Change order number') }}</v-card-title>
        <v-card-text>
          <v-alert v-if="rename.order && isSubmitted(rename.order)" type="warning" dense text class="mb-4">
            {{ $t('Already ordered at CPI under this number') }} ({{ rename.order.orderNumber }}).
            {{ $t('CPI keeps the old number; this only changes it here. The book will not be printed again.') }}
          </v-alert>
          <v-form ref="renameForm" @submit.prevent="saveRename">
            <v-text-field
              v-model="rename.orderNumber"
              :label="$t('Order number')"
              :rules="[required]"
              autofocus
              outlined
              dense
            />
          </v-form>
          <p class="text-caption mb-0">
            {{
              rename.order && isSubmitted(rename.order)
                ? $t('The book number follows the order number. The digital book is dropped — assemble it again before sending it.')
                : $t('The book number follows the order number. Assembled files are dropped — assemble again before submitting.')
            }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rename.show = false">{{ $t('Cancel') }}</v-btn>
          <v-btn color="primary" :loading="rename.saving" @click="saveRename">{{ $t('Save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="5000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import BirthPlaceField from '../components/BirthPlaceField.vue'
  import HoroscopeBookDialog from '../components/HoroscopeBookDialog.vue'
  import { placePayload } from '../components/birthPlace'
  import {
    createManualHoroscopeBook,
    addManualHoroscopeBook,
    getManualHoroscopeOrders,
    renameManualHoroscopeOrder,
  } from '../services/pdfGenerationService'
  import { getStoresProtected } from '../services/storeService'
  import { BOOK_TYPE_NAMES, GENDERS, bookTypeName, genderName } from '../components/horoscopeGeneration'

  /** The book designs rest-api knows (its BOOK_DESIGNS); replaced by the server's list once it answers. */
  const DESIGNS = [
    'PERSONALITY',
    'CHILDREN',
    'PROFESSION',
    'PARTNERS',
    'COSMIC-GLOW',
    'COSMIC-CALM',
    'MIDNIGHT-MAJESTY',
  ]

  const DATE_FORMAT = 'DD.MM.YYYY'
  const TIME_FORMAT = 'HH:mm'

  const emptyForm = () => ({
    orderNumber: '',
    variant: null,
    // null: the design's type (rest-api bookTypeOf)
    bookType: null,
    delivery: { print: true, digital: true },
    name: '',
    birthDate: '',
    birthTime: '',
    // { fullAddress, coordinates? } from BirthPlaceField
    birthPlace: null,
    // The subject's gender: gravur picks the pronouns by it (in a Kinder book the child's).
    gender: 'unknown',
    namePartner: '',
    birthDatePartner: '',
    birthTimePartner: '',
    birthPlacePartner: null,
    genderPartner: 'unknown',
    greetingMessage: '',
    customer: { firstName: '', lastName: '', email: '' },
    shipping: {
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      zip: '',
      city: '',
      country: '',
      countryCode: '',
    },
  })

  export default {
    name: 'ManageMizodiaCustom',
    components: { BirthPlaceField, HoroscopeBookDialog },
    data() {
      return {
        stores: [],
        selectedStore: null,
        designs: DESIGNS,
        form: emptyForm(),
        valid: false,
        creating: false,
        orders: [],
        loadingOrders: false,
        rename: { show: false, saving: false, order: null, orderNumber: '' },
        // The order a further book is being added to (after "Add another book").
        addingTo: null,
        another: { show: false, order: null, bookNumber: '', track: 'print' },
        headers: [
          { text: this.$t('Order number'), value: 'orderNumber' },
          { text: this.$t('Name'), value: 'name' },
          { text: this.$t('Cover'), value: 'variant' },
          { text: this.$t('Delivery'), value: 'tracks' },
          { text: this.$t('Stage'), value: 'stage' },
          { text: this.$t('Created'), value: 'createdAt' },
          { text: '', value: 'actions', align: 'end' },
        ],
        snackbar: { show: false, color: 'primary', message: '' },
        required: (v) => !!(v && String(v).trim()) || this.$t('A value is required'),
        /** CPI's drop-ship address needs a name; it may come from the customer fields instead. */
        shippingName: (field) => (v) =>
          !!((v && String(v).trim()) || String(this.form.customer[field] || '').trim()) ||
          this.$t('A value is required'),
        dateRule: (v) =>
          !v || moment.utc(v, DATE_FORMAT, true).isValid() || this.$t('Enter the date as DD.MM.YYYY'),
        timeRule: (v) =>
          !v || moment.utc(v, TIME_FORMAT, true).isValid() || this.$t('Enter the time as HH:mm'),
        emailRule: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || this.$t('Enter a valid email'),
        countryCodeRule: (v) => !v || /^[A-Za-z]{2}$/.test(v) || this.$t('Two letters, e.g. DE'),
      }
    },
    computed: {
      isPartners() {
        return this.form.variant === 'PARTNERS' || this.form.bookType === 'partner'
      },
      bookTypeItems() {
        return Object.keys(BOOK_TYPE_NAMES).map((type) => ({ value: type, text: this.$t(bookTypeName(type)) }))
      },
      genderItems() {
        return GENDERS.map((gender) => ({ value: gender, text: this.$t(genderName(gender)) }))
      },
      hasDelivery() {
        return this.form.delivery.print || this.form.delivery.digital
      },
    },
    async mounted() {
      this.stores = await getStoresProtected()
      const wanted = this.$route.query.store
      this.selectedStore =
        (wanted && this.stores.find((s) => s.id.toUpperCase() === String(wanted).toUpperCase())) ||
        this.stores[0] ||
        null
      await this.fetchOrders()
    },
    methods: {
      notify(message, color = 'primary') {
        this.snackbar = { show: true, message, color }
      },
      capitalize(s) {
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
      },
      formatDate(value) {
        return moment(value).format('DD.MM.YYYY HH:mm')
      },
      bookOf(order) {
        return (order.books && order.books[0]) || {}
      },
      has(order, delivery) {
        return (order.books || []).some((b) => (b.tracks || []).includes(delivery))
      },
      stageColor(book) {
        return (
          { created: 'grey', 'content-ready': 'blue', 'book-rendered': 'orange', submitted: 'green' }[
            book.stage
          ] || 'grey'
        )
      },
      /** What HoroscopeBookDialog reads from an order: id, number, customer, shipping address. */
      dialogOrder(order) {
        return {
          id: order.orderId,
          orderNumber: order.orderNumber,
          customer: order.customer || null,
          shippingAddress: order.shippingAddress || null,
          lineItems: [],
        }
      },
      async fetchOrders() {
        if (!this.selectedStore) return
        this.loadingOrders = true
        try {
          const { data } = await getManualHoroscopeOrders(this.selectedStore.id)
          this.orders = data.orders || []
          if (data.designs && data.designs.length) this.designs = data.designs
        } catch (error) {
          this.notify(this.errorMessage(error, 'Could not load the manual books'), 'error')
        } finally {
          this.loadingOrders = false
        }
      },
      /** Unix seconds (UTC) from the typed date and optional time — the same shape the dialog's edits send. */
      birthUnix(date, time) {
        return String(moment.utc(`${date} ${time || '00:00'}`, `${DATE_FORMAT} ${TIME_FORMAT}`, true).unix())
      },
      payload() {
        const f = this.form
        const trim = (v) => (v || '').trim()
        const properties = {
          name: trim(f.name),
          birthDate: this.birthUnix(f.birthDate, f.birthTime),
          showTime: !!trim(f.birthTime),
          birthPlace: placePayload(f.birthPlace),
          gender: f.gender || 'unknown',
        }
        if (trim(f.greetingMessage)) properties.greetingMessage = trim(f.greetingMessage)
        if (this.isPartners) {
          properties.namePartner = trim(f.namePartner)
          properties.birthDatePartner = this.birthUnix(f.birthDatePartner, f.birthTimePartner)
          properties.showTime2 = !!trim(f.birthTimePartner)
          properties.birthPlacePartner = placePayload(f.birthPlacePartner)
          properties.genderPartner = f.genderPartner || 'unknown'
        }

        const customer = {
          firstName: trim(f.customer.firstName),
          lastName: trim(f.customer.lastName),
          email: trim(f.customer.email),
        }
        const body = {
          orderNumber: trim(f.orderNumber),
          variant: f.variant,
          ...(f.bookType && { bookType: f.bookType }),
          // rest-api calls the bought tracks `package` (the Shopify `_package`); `delivery` is the email that went out.
          package: { print: !!f.delivery.print, digital: !!f.delivery.digital },
          properties,
        }
        if (Object.values(customer).some(Boolean)) body.customer = customer
        if (f.delivery.print) {
          body.shippingAddress = {
            firstName: trim(f.shipping.firstName) || customer.firstName,
            lastName: trim(f.shipping.lastName) || customer.lastName,
            address1: trim(f.shipping.address1),
            address2: trim(f.shipping.address2),
            zip: trim(f.shipping.zip),
            city: trim(f.shipping.city),
            country: trim(f.shipping.country),
            countryCode: trim(f.shipping.countryCode).toUpperCase(),
          }
        }
        return body
      },
      async create() {
        if (!this.$refs.form.validate() || !this.hasDelivery) return
        this.creating = true
        try {
          const payload = this.payload()
          let order
          let bookNumber
          if (this.addingTo) {
            // The order's number, customer and address are the server's; only the book goes along.
            const book = { ...payload }
            delete book.orderNumber
            delete book.customer
            delete book.shippingAddress
            const { data } = await addManualHoroscopeBook(this.selectedStore.id, this.addingTo.orderId, book)
            if (data.designs && data.designs.length) this.designs = data.designs
            order = {
              orderId: data.orderId,
              orderNumber: data.orderNumber,
              customer: data.customer || null,
              shippingAddress: data.shippingAddress || null,
              createdAt: data.createdAt || this.addingTo.createdAt,
              books: data.books || [],
            }
            bookNumber = data.bookNumber
          } else {
            const { data } = await createManualHoroscopeBook(this.selectedStore.id, payload)
            if (data.designs && data.designs.length) this.designs = data.designs
            order = {
              orderId: data.orderId,
              orderNumber: data.orderNumber,
              customer: payload.customer || null,
              shippingAddress: payload.shippingAddress || null,
              createdAt: new Date().toISOString(),
              books: data.books || [],
            }
            bookNumber = (data.books && data.books[0] && data.books[0].bookNumber) || data.orderNumber
          }
          this.orders = [order, ...this.orders.filter((o) => o.orderId !== order.orderId)]
          this.another = {
            show: true,
            order,
            bookNumber,
            track: this.form.delivery.print ? 'print' : 'digital',
          }
        } catch (error) {
          this.notify(this.errorMessage(error, 'Could not create the book'), 'error')
        } finally {
          this.creating = false
        }
      },
      /** Yes: same order, same delivery choice; only the book's own fields start empty. */
      acceptAnother() {
        const { order } = this.another
        const keep = this.form
        this.form = {
          ...emptyForm(),
          orderNumber: order.orderNumber,
          delivery: { ...keep.delivery, print: keep.delivery.print && !!order.shippingAddress },
          customer: keep.customer,
          shipping: keep.shipping,
        }
        this.addingTo = order
        this.another.show = false
        this.$nextTick(() => this.$refs.form.resetValidation())
      },
      /** No: back to an empty form, and the order's books open in the dialog. */
      async declineAnother() {
        const { order, track } = this.another
        this.another.show = false
        this.finishAdding()
        this.notify(this.$t('Book created'), 'success')
        await this.$nextTick()
        this.openDialog(order, track)
      },
      finishAdding() {
        this.addingTo = null
        this.form = emptyForm()
        this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
      },
      /** Ordered at CPI: renumbering is allowed, but CPI keeps the old number (the dialog warns). */
      isSubmitted(order) {
        return (order.books || []).some((b) => b.stage === 'submitted')
      },
      startRename(order) {
        this.rename = { show: true, saving: false, order, orderNumber: order.orderNumber }
        this.$nextTick(() => this.$refs.renameForm && this.$refs.renameForm.resetValidation())
      },
      async saveRename() {
        const { order } = this.rename
        const orderNumber = (this.rename.orderNumber || '').trim()
        if (!this.$refs.renameForm.validate() || !order) return
        if (orderNumber === order.orderNumber) {
          this.rename.show = false
          return
        }
        this.rename.saving = true
        try {
          const { data } = await renameManualHoroscopeOrder(this.selectedStore.id, order.orderId, orderNumber)
          this.orders = this.orders.map((o) => (o.orderId === order.orderId ? data : o))
          this.rename.show = false
          this.notify(this.$t('Order number changed'), 'success')
        } catch (error) {
          this.notify(this.errorMessage(error, 'Could not change the order number'), 'error')
        } finally {
          this.rename.saving = false
        }
      },
      /** Refs inside the table's row slot come back as arrays. */
      openDialog(order, track) {
        const ref = this.$refs[`${track}-${order.orderId}`]
        const dialog = Array.isArray(ref) ? ref[0] : ref
        if (dialog) dialog.open()
      },
      errorMessage(error, fallback) {
        const upstream = error.response && error.response.data && error.response.data.message
        return upstream || this.$t(fallback)
      },
    },
  }
</script>
